import { getLanguagePreference, setLanguagePreference } from '../utils/localStorageManager';
import zhHant from './zh-Hant';
import en from './en';
import { getLanguageTagDisplayName } from '../utils/languageTag.js';

// 可用的語言列表
const languages = {
    'zh-Hant': zhHant,
    'en': en
};

// 預設語言
const DEFAULT_LANGUAGE = 'zh-Hant';

// 當前活動語言
let activeLanguage = null;

/**
 * 初始化語言系統
 * @returns {Object} 已載入的語言包
 */
export function initLanguage() {
    const savedLanguage = getLanguagePreference() || DEFAULT_LANGUAGE;
    return switchLanguage(savedLanguage);
}

/**
 * 切換到新的語言
 * @param {string} langCode - 要切換到的語言代碼
 * @returns {Object} 已載入的語言包
 */
export function switchLanguage(langCode) {
    // 驗證語言是否存在
    if (!languages[langCode]) {
        console.warn(`找不到語言 ${langCode}，使用預設語言`);
        langCode = DEFAULT_LANGUAGE;
    }

    // 設置活動語言
    activeLanguage = languages[langCode];

    // 儲存偏好設定
    setLanguagePreference(langCode);

    // 觸發事件通知元件更新
    window.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { language: langCode }
    }));

    return activeLanguage;
}

/**
 * 獲取當前活動的語言包
 * @returns {Object} 當前語言包
 */
export function getLanguage() {
    if (!activeLanguage) {
        return initLanguage();
    }
    return activeLanguage;
}

/**
 * 獲取可用語言列表，用於語言選擇器
 * @returns {Array} 包含代碼和名稱的語言物件陣列
 */
export function getAvailableLanguages() {
    return Object.keys(languages).map(code => ({
        code,
        name: languages[code].languageName
    }));
}

/**
 * 獲取特定的翻譯字串
 * @param {string} key - 翻譯的點表示法路徑（例如：'common.welcome'）
 * @param {Object} params - 用於替換翻譯中的參數（可選）
 * @returns {string} 翻譯後的字串
 */
export function translate(key, params = {}) {
    const lang = getLanguage();

    // 用點號分割鍵以遍歷物件
    const keys = key.split('.');
    let value = lang;

    // 遍歷物件
    for (const k of keys) {
        if (value[k] === undefined) {
            console.warn(`找不到翻譯鍵：${key}`);
            return key;
        }
        value = value[k];
    }

    // 如果值不是字串，返回鍵
    if (typeof value !== 'string') {
        console.warn(`翻譯值不是字串：${key}`);
        return key;
    }

    // 替換參數
    let result = value;
    Object.entries(params).forEach(([paramKey, paramValue]) => {
        result = result.replace(new RegExp(`{${paramKey}}`, 'g'), paramValue);
    });

    return result;
}

/**
 * 獲取語言的顯示名稱
 * @param {string} langCode - 語言代碼（例如：'zh', 'en', 'ko', 'de'）
 * @returns {string} 語言的顯示名稱，如果找不到則返回語言代碼的大寫形式
 */
export function getLanguageDisplayName(langCode) {
    return getLanguageTagDisplayName(langCode);
}

// translate 的簡寫
export const t = translate;

export default {
    init: initLanguage,
    switch: switchLanguage,
    get: getLanguage,
    getAvailable: getAvailableLanguages,
    translate,
    t,
    getLanguageDisplayName
};
